import React, { useState } from 'react';

/**
 * InputModal Component
 * Reusable modal dialog for text input (replacement for prompt())
 */
function InputModal({ isOpen, onClose, onConfirm, title, placeholder, defaultValue = '', maxLength = 50 }) {
    const [value, setValue] = useState(defaultValue);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleConfirm = () => {
        const trimmedValue = value.trim();

        // Validation
        if (!trimmedValue) {
            setError('This field is required');
            return;
        }

        if (trimmedValue.length > maxLength) {
            setError(`Maximum ${maxLength} characters allowed`);
            return;
        }

        // Check for invalid characters
        if (!/^[a-zA-Z0-9\s_-]+$/.test(trimmedValue)) {
            setError('Only letters, numbers, spaces, hyphens, and underscores allowed');
            return;
        }

        onConfirm(trimmedValue);
        handleClose();
    };

    const handleClose = () => {
        setValue(defaultValue);
        setError('');
        onClose();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !error) {
            handleConfirm();
        } else if (e.key === 'Escape') {
            handleClose();
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
        }}>
            <div style={{
                background: 'var(--bgSecondary)',
                border: '1px solid var(--borderPrimary)',
                borderRadius: '8px',
                padding: '24px',
                width: '90%',
                maxWidth: '500px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
            }}>
                {/* Title */}
                <h2 style={{
                    margin: '0 0 16px 0',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'var(--textPrimary)'
                }}>
                    {title}
                </h2>

                {/* Input Field */}
                <input
                    type="text"
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setError(''); // Clear error on change
                    }}
                    onKeyDown={handleKeyPress}
                    placeholder={placeholder}
                    autoFocus
                    maxLength={maxLength + 10} // Allow typing a bit more for validation
                    style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        background: 'var(--bgTertiary)',
                        border: `1px solid ${error ? 'var(--danger)' : 'var(--borderSecondary)'}`,
                        borderRadius: '4px',
                        color: 'var(--textPrimary)',
                        outline: 'none',
                        marginBottom: '8px'
                    }}
                />

                {/* Error Message */}
                {error && (
                    <div style={{
                        color: 'var(--danger)',
                        fontSize: '12px',
                        marginBottom: '16px'
                    }}>
                        {error}
                    </div>
                )}

                {/* Character Count */}
                <div style={{
                    fontSize: '11px',
                    color: 'var(--textMuted)',
                    marginBottom: '16px'
                }}>
                    {value.length} / {maxLength} characters
                </div>

                {/* Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={handleClose}
                        style={{
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 500,
                            background: 'var(--bgTertiary)',
                            border: '1px solid var(--borderSecondary)',
                            borderRadius: '4px',
                            color: 'var(--textSecondary)',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'var(--bgSecondary)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'var(--bgTertiary)';
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!value.trim()}
                        style={{
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 500,
                            background: value.trim() ? 'var(--accent)' : 'var(--bgTertiary)',
                            border: `1px solid ${value.trim() ? 'var(--accent)' : 'var(--borderSecondary)'}`,
                            borderRadius: '4px',
                            color: value.trim() ? 'var(--textPrimary)' : 'var(--textMuted)',
                            cursor: value.trim() ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            if (value.trim()) {
                                e.target.style.opacity = '0.9';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.opacity = '1';
                        }}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}

export default InputModal;
